import { KPIPicker } from "@/components/kpi/KPIPicker";
import { useUnsentChatGuard } from "@/components/chat/UnsentChatGuard";
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TextField, TextArea, SelectField, DateField, CurrencyField } from "@/components/form/Fields";
import { MentionTextarea } from "@/components/form/MentionTextarea";
import { MultiSelect } from "@/components/form/MultiSelect";
import { useAuth } from "@/context/AuthContext";
import { quickAddOrg } from "@/components/hooks/useOptions";
import client, { formatApiErrorDetail } from "@/lib/api";
import { CAMPAIGN_STATUSES, CAMPAIGN_MARKETS, CAMPAIGN_TYPES } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, Trash2, CalendarRange, ListChecks } from "lucide-react";
import { LinkedTasksPanel } from "@/components/shared/LinkedTasksPanel";
import { RecurringScopeDialog } from "@/components/shared/RecurringScopeDialog";
import { ChatConversation } from "@/components/chat/ChatConversation";
import { cn } from "@/lib/utils";
import { ModalNotes } from "@/components/shared/ModalNotes";
import { CampaignOppLinks } from "@/components/opportunities/CampaignLinks";
import { CampaignKindToggle, RegionPropertyPicker, TOURS_STATUSES } from "@/components/campaign/ToursFields";
import { usePermissions } from "@/context/PermissionsContext";

function CampaignChatTab({ campaignId }) {
  const [chatId, setChatId] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    client.post(`/chats/from-campaign/${campaignId}`).then(({ data }) => setChatId(data.id)).catch(() => setErr(true));
  }, [campaignId]);
  if (err) return <div className="py-10 text-center text-sm text-slate-400">Could not load chat</div>;
  if (!chatId) return <div className="py-10 text-center text-sm text-slate-400">Loading chat…</div>;
  return <div className="h-[60vh] w-full min-w-0 max-w-full overflow-hidden rounded-lg border border-slate-200" data-testid="campaign-chat-tab"><ChatConversation chatId={chatId} embedded /></div>;
}

const CAMPAIGN_STATUS_STYLES = {
  New: "bg-slate-50 text-slate-600 border-slate-200",
  Preparing: "bg-amber-50 text-amber-700 border-amber-200",
  Complete: "bg-indigo-50 text-indigo-700 border-indigo-200",
  "In Discussions": "bg-amber-50 text-amber-700 border-amber-200",
  Scheduled: "bg-sky-50 text-sky-700 border-sky-200",
  "ON SALE": "bg-emerald-50 text-emerald-700 border-emerald-200",
  Completed: "bg-indigo-50 text-indigo-700 border-indigo-200",
  Extended: "bg-violet-50 text-violet-700 border-violet-200",
  Cancelled: "bg-rose-50 text-rose-700 border-rose-200",
};

export function CampaignStatusPill({ status }) {
  const cls = CAMPAIGN_STATUS_STYLES[status] || "bg-slate-50 text-slate-600 border-slate-200";
  return <span className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium leading-4 ${cls}`} data-testid="campaign-status-pill">{status || "—"}</span>;
}

const today = () => new Date().toISOString().slice(0, 10);
const localIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const WD_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const weekdayOf = (d) => {
  if (!d) return "";
  const dt = new Date(`${d}T00:00:00`);
  return isNaN(dt) ? "" : WD_NAMES[dt.getDay()];
};

function RangeList({ label, value = [], onChange, testid }) {  const rows = value.length ? value : [];
  const update = (i, k, v) => onChange(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const add = () => onChange([...rows, { start: null, end: null }]);
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  return (
    <div data-testid={testid}>
      <div className="mb-1.5 flex items-center justify-between">
        <label className="text-xs font-semibold text-slate-600">{label}</label>
        <button type="button" onClick={add} data-testid={`${testid}-add`} className="flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold text-sky-600 hover:bg-sky-50">
          <Plus className="h-3.5 w-3.5" /> Add range
        </button>
      </div>
      {rows.length === 0 ? (
        <p className="rounded-md border border-dashed border-slate-200 px-3 py-2 text-xs text-slate-400">No ranges added.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="flex-1"><DateField inline label="From" value={r.start} onChange={(v) => update(i, "start", v)} testid={`${testid}-${i}-start`} /></div>
              <div className="flex-1"><DateField inline label="To" value={r.end} onChange={(v) => update(i, "end", v)} testid={`${testid}-${i}-end`} monthHint={r.start} /></div>
              <button type="button" onClick={() => remove(i)} data-testid={`${testid}-${i}-remove`} className="rounded-md p-2 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DateList({ label, value = [], onChange, labels = {}, onLabels, testid }) {
  const rows = value;
  const update = (i, v) => {
    const prev = rows[i];
    onChange(rows.map((r, idx) => (idx === i ? v : r)));
    if (prev && labels[prev] && v && !rows.includes(v)) {
      const { [prev]: moved, ...rest } = labels;
      onLabels({ ...rest, [v]: moved });
    }
  };
  const setLabel = (d, text) => { if (d) onLabels({ ...labels, [d]: text }); };
  const add = () => onChange([...rows, null]);
  const remove = (i) => {
    const d = rows[i];
    onChange(rows.filter((_, idx) => idx !== i));
    if (d && labels[d] != null) { const { [d]: _gone, ...rest } = labels; onLabels(rest); }
  };
  return (
    <div data-testid={testid}>
      <div className="mb-1 flex items-center justify-between">
        <label className="text-xs font-semibold text-slate-600">{label}</label>
        <button type="button" onClick={add} data-testid={`${testid}-add`} className="flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold text-sky-600 hover:bg-sky-50">
          <Plus className="h-3.5 w-3.5" /> Add date
        </button>
      </div>
      {rows.length === 0 ? (
        <p className="rounded-md border border-dashed border-slate-200 px-3 py-2 text-xs text-slate-400">No resend dates added.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="flex-1"><DateField inline label={`Resend ${i + 1}`} value={r} onChange={(v) => update(i, v)} testid={`${testid}-${i}`} /></div>
              <input value={(r && labels[r]) || ""} onChange={(e) => setLabel(r, e.target.value)} disabled={!r}
                placeholder={r ? "Label, e.g. 2 Day Resend" : "Pick a date first"} data-testid={`${testid}-${i}-label`}
                className="w-48 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100 disabled:bg-slate-50" />
              <button type="button" onClick={() => remove(i)} data-testid={`${testid}-${i}-remove`} className="rounded-md p-2 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function CampaignModal({ open, record, prefill = {}, onClose, onSuccess }) {
  const [guard, unsentDialog] = useUnsentChatGuard();
  const { user, users } = useAuth();
  const { can } = usePermissions();
  const [orgs, setOrgs] = useState([]);
  const [form, setForm] = useState({ status: "New", markets: [], travel_dates: [], blockout_dates: [] });
  const [saving, setSaving] = useState(false);
  const [days, setDays] = useState(null);
  const [savedRec, setSavedRec] = useState(null);
  const [stopOpen, setStopOpen] = useState(false);
  const rec = record?.id ? record : savedRec;

  useEffect(() => {
    if (!open) return;
    setSavedRec(null);
    setForm(record || { status: "New", markets: [], travel_dates: [], blockout_dates: [], resend_dates: [], subtasks: [], ...prefill });
    setDays(null);
    client.get("/organisations", { params: { lite: true } }).then(({ data }) => setOrgs(data)).catch(() => {});
  }, [open, record]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  // EDM: only full days count (starts mid-day, so 7 days ends at midnight on the same weekday next week)
  const fullDays = (form.campaign_type || "Agent") === "Email";
  const durationDays = form.on_sale_start && form.on_sale_end && form.on_sale_end >= form.on_sale_start
    ? Math.round((new Date(`${form.on_sale_end}T00:00:00`) - new Date(`${form.on_sale_start}T00:00:00`)) / 86400000) + (fullDays ? 0 : 1) : "";
  const endFrom = (start, n) => { const d = new Date(`${start}T00:00:00`); d.setDate(d.getDate() + n - (fullDays ? 0 : 1)); return localIso(d); };
  const setDuration = (v) => {
    setDays(v);
    const n = parseInt(v, 10);
    if (form.on_sale_start && n >= 1) set("on_sale_end")(endFrom(form.on_sale_start, n));
  };
  const setStart = (v) => {
    const n = parseInt(days ?? durationDays, 10);
    setForm((f) => ({ ...f, on_sale_start: v, ...(v && n >= 1 ? { on_sale_end: endFrom(v, n) } : {}) }));
  };
  const isEdit = Boolean(rec?.id);
  const [tab, setTab] = useState("details");
  const isEmail = (form.campaign_type || "Agent") === "Email";
  const isTours = isEmail && !!form.tours_dtr;
  const repeats = isTours && (form.repeat_every || 0) >= 1;
  const toggleRepeats = (on) => {
    if (on) return setForm((f) => ({ ...f, repeat_every: f.repeat_every || 1, repeat_unit: f.repeat_unit || "weeks" }));
    if (rec?.series_id) return setStopOpen(true);
    setForm((f) => ({ ...f, repeat_every: null, repeat_unit: null }));
  };
  const pickStop = async (k) => {
    setStopOpen(false);
    try {
      const { data } = await client.post(`/campaigns/${rec.id}/stop-series`, { delete_future: k === "delete" });
      setForm((f) => ({ ...f, repeat_every: null, repeat_unit: null, series_id: null }));
      toast.success(k === "delete" ? `Stopped repeating, removed ${data.removed} future campaign${data.removed === 1 ? "" : "s"}` : "Stopped repeating");
      onSuccess && onSuccess();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const toursStatusOpts = form.status === "Complete" ? [...TOURS_STATUSES, "Complete"] : TOURS_STATUSES;
  const setKind = (v) => setForm((f) => ({ ...f, tours_dtr: v, status: (v ? TOURS_STATUSES : CAMPAIGN_STATUSES).includes(f.status) ? f.status : "New" }));
  const computedWeekday = isEmail ? weekdayOf(form.on_sale_start) : null;
  const bkNum = (v) => (v === "" || v == null ? 0 : Number(v) || 0);
  const emailBookingsTotal = bkNum(form.results_online_bookings) + bkNum(form.results_social_bookings) + bkNum(form.results_why_book_direct) + bkNum(form.results_popup_bookings);
  const userOpts = users.map((u) => ({ value: u.id, label: u.name }));

  const agentOpts = orgs.map((o) => ({ value: o.id, label: o.name }));
  const propertyOpts = orgs.filter((o) => o.org_type === "DTR Property" || o.name === "Dreamtime Resorts").map((o) => ({ value: o.id, label: o.name }));

  const toggleMarket = (m) => {
    const cur = form.markets || [];
    set("markets")(cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m]);
  };

  const toggleNotified = (checked) => {
    setForm((f) => ({
      ...f,
      property_notified: checked,
      property_notified_date: checked ? (f.property_notified_date || today()) : f.property_notified_date,
      property_notified_by_id: checked ? (f.property_notified_by_id || user?.id) : f.property_notified_by_id,
    }));
  };

  const submit = async (keepOpen = false) => {
    if (!form.name?.trim()) return toast.error("Please enter a Campaign Name");
    setSaving(true);
    try {
      const num = (v) => (v === "" || v == null ? null : Number(v));
      const emailOnly = isEmail ? { markets: [], market_other: null, redeem_by: null, blockout_dates: [] } : {
        subject: null, send_weekday: null, send_time: null,
        results_open_rate: null, results_unsubscribes: null, results_online_bookings: null,
        results_social_bookings: null, results_why_book_direct: null, results_popup_bookings: null, results_als: null,
      };
      const payload = {
        ...form,
        resend_dates: (form.resend_dates || []).filter(Boolean),
        resend_labels: Object.fromEntries(Object.entries(form.resend_labels || {}).filter(([d, t]) => (form.resend_dates || []).includes(d) && t && t.trim()).map(([d, t]) => [d, t.trim()])),
        send_weekday: isEmail ? (computedWeekday || null) : null,
        results_bookings: isEmail ? ([form.results_online_bookings, form.results_social_bookings, form.results_why_book_direct, form.results_popup_bookings].some((v) => v !== "" && v != null) ? emailBookingsTotal : null) : num(form.results_bookings),
        results_room_nights: num(form.results_room_nights),
        results_revenue: num(form.results_revenue),
        results_open_rate: num(form.results_open_rate),
        results_unsubscribes: num(form.results_unsubscribes),
        results_online_bookings: num(form.results_online_bookings),
        results_social_bookings: num(form.results_social_bookings),
        results_why_book_direct: num(form.results_why_book_direct),
        results_popup_bookings: num(form.results_popup_bookings),
        results_als: num(form.results_als),
        ...emailOnly,
        ...(isTours ? {
          property_id: null, on_sale_end: form.on_sale_start || null, resend_dates: [], resend_labels: {}, travel_dates: [],
          results_bookings: null, results_room_nights: null, results_revenue: null, results_open_rate: null, results_unsubscribes: null,
          results_online_bookings: null, results_social_bookings: null, results_why_book_direct: null, results_popup_bookings: null, results_als: null,
        } : { tours_dtr: false, group_ids: [], property_ids: [], repeat_every: null, repeat_unit: null, target_audience: null }),
      };
      delete payload.series_id;
      if (isEdit) delete payload.notes;
      if (isEdit) await client.patch(`/campaigns/${rec.id}`, payload);
      else {
        const { data } = await client.post("/campaigns", payload);
        if (keepOpen) {
          setSavedRec(data);
          setForm(data);
          toast.success("Campaign created. You can add tasks now.");
          onSuccess && onSuccess();
          return;
        }
      }
      toast.success(isEdit ? "Campaign updated" : "Campaign created");
      onSuccess && onSuccess();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && guard(onClose)()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="campaign-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <CalendarRange className="h-5 w-5 text-sky-600" />
            {isEdit ? "Edit Campaign" : "New Campaign"}
          </DialogTitle>
          <DialogDescription className="sr-only">Campaign details form</DialogDescription>
        </DialogHeader>
        {isEdit && (
          <div className="flex gap-1 border-b border-slate-100 pb-2" data-testid="cmp-tabs">
            {[["details", "Details"], ["chat", "Chat"]].map(([v, l]) => (
              <button key={v} onClick={() => setTab(v)} data-testid={`cmp-tab-${v}`}
                className={cn("rounded-lg px-3 py-1.5 text-sm font-medium transition-colors", tab === v ? "bg-sky-50 text-sky-700" : "text-slate-500 hover:bg-slate-50")}>{l}</button>
            ))}
          </div>
        )}
        {isEdit && tab === "chat" ? <CampaignChatTab campaignId={rec.id} /> : (
        <div className="space-y-4">
          <TextField label="Campaign Name" required value={form.name} onChange={set("name")} testid="cmp-field-name" placeholder="e.g. Winter Escape 2026" />
          {isEmail && <CampaignKindToggle value={form.tours_dtr} onChange={setKind} />}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <SelectField label="Campaign Type" value={form.campaign_type || "Agent"} onChange={set("campaign_type")} options={CAMPAIGN_TYPES} allowEmpty={false} testid="cmp-field-type" />
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-600">Assigned Users</label>
              <MultiSelect value={form.user_ids || []} onChange={set("user_ids")} options={userOpts} testid="cmp-field-users" placeholder="Link users…" />
            </div>
          </div>
          {(form.campaign_type || "Agent") === "Agent" && can("kpis", "view") && <KPIPicker value={form.kpi_ids || []} onChange={set("kpi_ids")} userIds={form.user_ids || []} testid="cmp-field-kpis" />}

          {isTours ? (
            <>
              <SelectField label="Status" value={form.status} onChange={set("status")} options={toursStatusOpts} allowEmpty={false} testid="cmp-field-status" />
              <RegionPropertyPicker groupIds={form.group_ids || []} propertyIds={form.property_ids || []} orgs={orgs}
                onChange={(v) => setForm((f) => ({ ...f, ...v }))} />
              <TextField label="Subject" value={form.subject} onChange={set("subject")} testid="cmp-field-subject" placeholder="Email subject line" />
              <TextField label="Target Audience" value={form.target_audience} onChange={set("target_audience")} testid="cmp-field-target-audience" placeholder="e.g. Past guests 2024-25, QLD residents" />
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="cmp-tours-send">
                <DateField label="Send Date" value={form.on_sale_start} onChange={set("on_sale_start")} testid="cmp-field-send-date" />
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-600" htmlFor="cmp-tours-time">Send Time</label>
                  <input id="cmp-tours-time" type="time" value={form.send_time || ""} onChange={(e) => set("send_time")(e.target.value || null)} data-testid="cmp-field-send-time"
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100" />
                </div>
              </div>
              <p className="-mt-2 text-[11px] text-slate-400">Status changes to Complete automatically once the send date and time has passed.</p>
              <div className="rounded-lg border border-slate-200 p-3" data-testid="cmp-repeat">
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input type="checkbox" checked={repeats} onChange={(e) => toggleRepeats(e.target.checked)} data-testid="cmp-field-repeats" className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-400" />
                  Repeats
                </label>
                {repeats && (
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-slate-600">
                    Every
                    <input type="number" min={1} value={form.repeat_every || ""} onChange={(e) => set("repeat_every")(Math.max(1, parseInt(e.target.value, 10) || 1))} data-testid="cmp-field-repeat-every"
                      className="w-20 rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:border-sky-400 focus:outline-none" />
                    <select value={form.repeat_unit || "weeks"} onChange={(e) => set("repeat_unit")(e.target.value)} data-testid="cmp-field-repeat-unit"
                      className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm focus:border-sky-400 focus:outline-none">
                      <option value="days">day(s)</option><option value="weeks">week(s)</option><option value="months">month(s)</option>
                    </select>
                    <span className="text-[11px] text-slate-400">A separate campaign is created for each send, kept 12 months ahead, until Repeats is unticked.</span>
                  </div>
                )}
              </div>
            </>
          ) : isEmail ? (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <SelectField label="Property" value={form.property_id} onChange={set("property_id")} options={propertyOpts} placeholder="Choose property…" testid="cmp-field-property" />
                <SelectField label="Status" value={form.status} onChange={set("status")} options={CAMPAIGN_STATUSES} allowEmpty={false} testid="cmp-field-status" />
              </div>
              <TextField label="Subject" value={form.subject} onChange={set("subject")} testid="cmp-field-subject" placeholder="Email subject line" />
            </>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600">Agent (Organisation)</label>
                <MultiSelect
                  value={form.agent_id ? [form.agent_id] : []}
                  onChange={(arr) => set("agent_id")(arr[arr.length - 1] || null)}
                  options={agentOpts}
                  onQuickAdd={quickAddOrg}
                  testid="cmp-field-agent"
                  placeholder="Search or quick-add agent…"
                />
              </div>
              <SelectField label="Property" value={form.property_id} onChange={set("property_id")} options={propertyOpts} placeholder="Choose property…" testid="cmp-field-property" />
            </div>
          )}

          {!isEmail && (
            <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600">Market</label>
                <div className="flex flex-wrap gap-2">
                  {CAMPAIGN_MARKETS.map((m) => {
                    const active = (form.markets || []).includes(m);
                    return (
                      <button
                        key={m}
                        type="button"
                        data-testid={`cmp-market-${m}`}
                        onClick={() => toggleMarket(m)}
                        className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${active ? "border-sky-500 bg-sky-50 text-sky-700" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}
                      >
                        {m}
                      </button>
                    );
                  })}
                </div>
                {(form.markets || []).includes("OTHER") && (
                  <div className="mt-2">
                    <TextField value={form.market_other} onChange={set("market_other")} testid="cmp-field-market-other" placeholder="Specify other market(s), e.g. USA" />
                  </div>
                )}
              </div>
              <SelectField label="Status" value={form.status} onChange={set("status")} options={CAMPAIGN_STATUSES} allowEmpty={false} testid="cmp-field-status" />
            </div>
          )}

          {!isEmail && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <DateField label="EOI Date" value={form.eoi_date} onChange={set("eoi_date")} testid="cmp-field-eoi-date" />
              <DateField label="Submission Date" value={form.submission_date} onChange={set("submission_date")} testid="cmp-field-submission-date" />
            </div>
          )}

          {!isTours && <div>
            <label className="mb-1.5 block text-xs font-semibold text-slate-600">On Sale</label>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-6">
              <div className="col-span-2 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 sm:col-span-2">
                <label className="shrink-0 text-xs font-semibold text-slate-500" htmlFor="cmp-days"># Days</label>
                <input id="cmp-days" type="number" min="1" value={days ?? durationDays} onChange={(e) => setDuration(e.target.value)}
                  placeholder="e.g. 7" data-testid="cmp-field-days"
                  className="w-full min-w-0 flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-300" />
              </div>
              <div className="sm:col-span-2"><DateField inline label="From" value={form.on_sale_start} onChange={setStart} testid="cmp-field-onsale-start" /></div>
              <div className="sm:col-span-2"><DateField inline label="To" value={form.on_sale_end} onChange={(v) => { setDays(null); set("on_sale_end")(v); }} testid="cmp-field-onsale-end" monthHint={form.on_sale_start} /></div>
              {isEmail ? (
                <>
                  <div className="col-span-1 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 sm:col-span-3">
                    <label className="shrink-0 text-xs font-semibold text-slate-500">Send Weekday</label>
                    <span className="min-w-0 flex-1 text-sm text-slate-900" data-testid="cmp-field-weekday">{computedWeekday || "—"}</span>
                  </div>
                  <div className="col-span-1 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 sm:col-span-3">
                    <label className="shrink-0 text-xs font-semibold text-slate-500" htmlFor="cmp-send-time">Send Time</label>
                    <input id="cmp-send-time" type="time" value={form.send_time || ""} onChange={(e) => set("send_time")(e.target.value || null)} data-testid="cmp-field-send-time"
                      className="min-w-0 flex-1 bg-transparent text-sm text-slate-900 outline-none" />
                  </div>
                </>
              ) : (
                <div className="col-span-2 sm:col-span-6"><DateField inline label="Redeem By" value={form.redeem_by} onChange={set("redeem_by")} testid="cmp-field-redeem" /></div>
              )}
            </div>
          </div>}
          {!isTours && <DateList label="Resend Dates" value={form.resend_dates} onChange={set("resend_dates")} labels={form.resend_labels || {}} onLabels={set("resend_labels")} testid="cmp-resend" />}

          {!isTours && <RangeList label="Travel Dates" value={form.travel_dates} onChange={set("travel_dates")} testid="cmp-travel" />}
          {!isEmail && <RangeList label="Blockout Dates" value={form.blockout_dates} onChange={set("blockout_dates")} testid="cmp-blockout" />}

          <TextArea label="Campaign Details" value={form.details} onChange={set("details")} rows={3} testid="cmp-field-details" />

          {!isEmail && (
            <div className="grid grid-cols-1 gap-3 rounded-lg border border-slate-200 p-3 sm:grid-cols-2" data-testid="cmp-review-flags">
              {[["partner_report_received", "Partner Report Received"], ["review_completed", "Review Completed"]].map(([k, l]) => (
                <div key={k}>
                  <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                    <input type="checkbox" data-testid={`cmp-field-${k}`} checked={!!form[k]} onChange={(e) => set(k)(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-400" />
                    {l}
                  </label>
                  {form[k] && form[`${k}_at`] && (
                    <p className="mt-1 pl-6 text-[11px] text-slate-400" data-testid={`cmp-${k}-meta`}>
                      {users.find((u) => u.id === form[`${k}_by_id`])?.name || "Someone"} · {new Date(form[`${k}_at`]).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" })}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="rounded-lg border border-slate-200 p-3">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <input type="checkbox" data-testid="cmp-field-notified" checked={!!form.property_notified} onChange={(e) => toggleNotified(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-400" />
              {isTours ? "Res Team notified?" : "Property notified?"}
            </label>
            {form.property_notified && (
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <DateField label="Notified On" value={form.property_notified_date} onChange={set("property_notified_date")} testid="cmp-field-notified-date" />
                <SelectField label="Notified By" value={form.property_notified_by_id} onChange={set("property_notified_by_id")} options={users.map((u) => ({ value: u.id, label: u.name }))} testid="cmp-field-notified-by" />
              </div>
            )}
          </div>

          {isEdit && !isEmail && <CampaignOppLinks campaignId={rec.id} />}

          {isEdit ? (
            <LinkedTasksPanel parentType={isEmail ? "edm_campaign" : "agent_campaign"} parentId={rec.id} parentLabel={form.name}
              prefill={{ property_ids: !isTours && form.property_id ? [form.property_id] : [], assigned_to_ids: form.user_ids?.length ? form.user_ids : undefined }} />
          ) : (
            <div data-testid="cmp-tasks-new">
              <div className="mb-2 flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-600"><ListChecks className="h-3.5 w-3.5" />Tasks</span>
                <button type="button" onClick={guard(() => submit(true))} disabled={saving} data-testid="cmp-tasks-save-first"
                  className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-sky-600 hover:bg-sky-50"><Plus className="h-3.5 w-3.5" />Save &amp; add tasks</button>
              </div>
              <p className="rounded-lg border border-dashed border-slate-200 py-3 text-center text-xs text-slate-400">Tasks appear on the assignee's To Do list and link back to this campaign.</p>
            </div>
          )}

          {!isTours && <div>
            <label className="mb-1.5 block text-xs font-semibold text-slate-600">Campaign Results</label>
            {isEmail ? (
              <div className="rounded-lg border border-sky-200 bg-sky-100/60 p-3">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                  <TextField label="Room Nts" type="number" value={form.results_room_nights} onChange={set("results_room_nights")} testid="cmp-field-roomnights" />
                  <CurrencyField label="Revenue (AUD)" value={form.results_revenue} onChange={set("results_revenue")} testid="cmp-field-revenue" />
                  <TextField label="Open Rate (%)" type="number" value={form.results_open_rate} onChange={set("results_open_rate")} testid="cmp-field-openrate" />
                  <TextField label="Unsubscribes" type="number" value={form.results_unsubscribes} onChange={set("results_unsubscribes")} testid="cmp-field-unsubscribes" />
                  <TextField label="ALS" type="number" step="0.1" value={form.results_als} onChange={set("results_als")} testid="cmp-field-als" />
                </div>
                <div className="mt-3 flex items-center justify-between border-t border-sky-200 pt-3">
                  <span className="text-xs font-semibold text-slate-600">Bookings Breakdown</span>
                  <span className="text-xs font-semibold text-slate-700" data-testid="cmp-bookings-total">Total Bookings: {emailBookingsTotal}</span>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <TextField label="Online" labelClassName="text-[11px] font-medium text-slate-500" type="number" value={form.results_online_bookings} onChange={set("results_online_bookings")} testid="cmp-field-onlinebookings" />
                  <TextField label="Social" labelClassName="text-[11px] font-medium text-slate-500" type="number" value={form.results_social_bookings} onChange={set("results_social_bookings")} testid="cmp-field-socialbookings" />
                  <TextField label="WBD/Gold" labelClassName="text-[11px] font-medium text-slate-500" type="number" value={form.results_why_book_direct} onChange={set("results_why_book_direct")} testid="cmp-field-whybookdirect" />
                  <TextField label="Website Popup" labelClassName="text-[11px] font-medium text-slate-500" type="number" value={form.results_popup_bookings} onChange={set("results_popup_bookings")} testid="cmp-field-popupbookings" />
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-sky-200 bg-sky-100/60 p-3" data-testid="cmp-agent-results">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <TextField label="Bookings" type="number" value={form.results_bookings} onChange={set("results_bookings")} testid="cmp-field-bookings" />
                  <TextField label="Room Nights" type="number" value={form.results_room_nights} onChange={set("results_room_nights")} testid="cmp-field-roomnights" />
                  <CurrencyField label="Total Revenue (AUD)" value={form.results_revenue} onChange={set("results_revenue")} testid="cmp-field-revenue" />
                </div>
              </div>
            )}
          </div>}

          <ModalNotes entityType="campaign" collection="campaigns" record={rec} value={form.notes} onChange={set("notes")} testid="cmp-field-notes" />
        </div>
        )}
        {unsentDialog}
        <RecurringScopeDialog open={stopOpen} onOpenChange={setStopOpen} title="Stop repeating"
          description="This campaign won't repeat any more. What should happen to the future campaigns in this series that haven't been sent?"
          options={[["keep", "Keep future campaigns"], ["delete", "Delete future campaigns"]]} onPick={pickStop} />
        <DialogFooter>
          <Button variant="outline" onClick={guard(onClose)}>Cancel</Button>
          <Button className="bg-sky-600 hover:bg-sky-700" onClick={guard(() => submit())} disabled={saving} data-testid="cmp-save-button">
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Campaign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
